import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import type { Content, TDocumentDefinitions } from 'pdfmake/interfaces';
import { safeGetHostname } from './urlUtils';
import htmlToPdfmake from 'html-to-pdfmake';

pdfMake.addVirtualFileSystem(pdfFonts);

interface ExportPdfParams {
  content: string;
  title: string;
  timestamp?: Date;
  language?: 'fi' | 'en' | 'sv';
  conversationId?: string;
}

/**
 * Converts markdown text to HTML format
 * @param markdown - The markdown text to convert
 * @returns HTML string with proper tags for formatting
 */
function markdownToHtml(markdown: string): string {
  let html = markdown;

  // Convert headings (## Heading)
  html = html.replace(/^## (.+)$/gm, '<h2>$1</h2>');

  // Convert bold text (**text**) - process before italic to avoid conflicts
  html = html.replace(/\*\*([^*\n]+?)\*\*/g, '<strong>$1</strong>');

  // Convert italic text (*text*) - use more specific pattern to avoid conflicts
  // This pattern ensures we don't match asterisks that are part of bold markers
  html = html.replace(/(?<!\*)\*([^*\n]+?)\*(?!\*)/g, '<em>$1</em>');

  // Convert bullet points to proper list items
  const lines = html.split('\n');
  const processedLines: string[] = [];
  let inList = false;

  for (const line of lines) {
    const trimmedLine = line.trim();

    if (trimmedLine.startsWith('- ')) {
      // Start or continue a list
      if (!inList) {
        processedLines.push('<ul>');
        inList = true;
      }
      // Remove the "- " prefix and wrap in list item
      processedLines.push(`<li>${trimmedLine.substring(2)}</li>`);
    } else {
      // End list if we were in one
      if (inList && trimmedLine !== '') {
        processedLines.push('</ul>');
        inList = false;
      }

      // Add the line (skip empty lines that are just for spacing)
      if (trimmedLine !== '' || !inList) {
        // Wrap non-list, non-heading content in paragraphs
        if (trimmedLine && !trimmedLine.startsWith('<h') && !trimmedLine.startsWith('<ul')) {
          processedLines.push(`<p>${trimmedLine}</p>`);
        } else {
          processedLines.push(line);
        }
      }
    }
  }

  // Close list if still open at the end
  if (inList) {
    processedLines.push('</ul>');
  }

  return processedLines.join('\n');
}

/**
 * Exports a markdown report as a PDF file using PDFMake
 * Uses standard PDF fonts (Helvetica) for instant generation without font loading
 * @param params Export parameters including content, title, and optional timestamp
 * @returns void
 * @throws Error if PDF generation fails
 */
export function exportReportAsPdf({
  content,
  title,
  timestamp = new Date(),
  conversationId,
}: ExportPdfParams): void {
  try {
    const htmlContent = markdownToHtml(content);

    const pdfMakeContent = htmlToPdfmake(htmlContent, {
      defaultStyles: {
        h2: { fontSize: 14, bold: true, margin: [0, 10, 0, 5] },
        p: { fontSize: 11, margin: [0, 0, 0, 5] },
        strong: { bold: true },
        em: { italics: true },
        ul: { margin: [0, 5, 0, 5] },
        li: { fontSize: 11, margin: [10, 0, 0, 0] },
      },
    });

    const dateStr = timestamp.toLocaleDateString('fi-FI');
    const timeStr = timestamp.toLocaleTimeString('fi-FI', { hour: '2-digit', minute: '2-digit' });

    const documentDefinition: TDocumentDefinitions = {
      content: [
        { text: title, style: 'title', margin: [0, 0, 0, 10] },
        { text: `${dateStr} ${timeStr}`, style: 'subtitle', margin: [0, 0, 0, 5] },
        ...(conversationId
          ? [
              {
                text: `ID: ${conversationId}`,
                style: 'subtitle',
                margin: [0, 0, 0, 15] as [number, number, number, number],
              },
            ]
          : []),
        ...(Array.isArray(pdfMakeContent) ? pdfMakeContent : [pdfMakeContent]),
      ],

      styles: {
        title: {
          fontSize: 18,
          bold: true,
          color: '#212121',
        },
        subtitle: {
          fontSize: 10,
          color: '#646464',
        },
      },

      defaultStyle: {
        fontSize: 11,
        color: '#3C3C3C',
      },

      pageSize: 'A4',
      pageMargins: [40, 40, 40, 40],
    };

    const timestampStr = timestamp.toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const filename = `operator-chat-summary-${timestampStr}.pdf`;

    pdfMake.createPdf(documentDefinition).download(filename);
  } catch (error) {
    console.error('PDF generation failed:', error);
    throw new Error(
      `Failed to generate PDF: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}

interface SurveyReportParams {
  victimProfile: string;
  crimeOverview: string;
  immediateActions: string[];
  helpSources: Array<{
    name: string;
    description: string;
    contacts: Array<{
      type: 'url' | 'phone';
      value: string;
    }>;
    source: 'online' | 'offline';
  }>;
  timestamp?: Date;
  sectionTitles: {
    victimProfile: string;
    crimeOverview: string;
    immediateActions: string;
    helpSources: string;
    onlineHelp: string;
    offlineHelp: string;
  };
  documentTitle: string;
}

/**
 * Exports a survey report as a PDF file using PDFMake
 * Formats survey response data (victim profile, crime overview, actions, help sources) into a structured PDF
 * @param params Survey report parameters including all response sections
 * @returns void
 * @throws Error if PDF generation fails
 */
export function exportSurveyReportAsPdf(params: SurveyReportParams): void {
  try {
    const {
      victimProfile,
      crimeOverview,
      immediateActions,
      helpSources,
      timestamp = new Date(),
      sectionTitles,
      documentTitle,
    } = params;

    const dateStr = timestamp.toLocaleDateString('fi-FI');
    const timeStr = timestamp.toLocaleTimeString('fi-FI', { hour: '2-digit', minute: '2-digit' });

    const victimProfileContent = htmlToPdfmake(markdownToHtml(victimProfile), {
      defaultStyles: {
        p: { fontSize: 11, margin: [0, 0, 0, 5] },
        strong: { bold: true },
        em: { italics: true },
      },
    });

    const crimeOverviewContent = htmlToPdfmake(markdownToHtml(crimeOverview), {
      defaultStyles: {
        p: { fontSize: 11, margin: [0, 0, 0, 5] },
        strong: { bold: true },
        em: { italics: true },
      },
    });

    const addHelpSourceSection = (
      sources: SurveyReportParams['helpSources'],
      title: string,
      targetContent: Content[]
    ): void => {
      if (sources.length === 0) return;

      targetContent.push({
        text: title,
        style: 'subsection',
        margin: [0, 10, 0, 5],
      });

      sources.forEach((source) => {
        const contactsContent = source.contacts.map((contact) => ({
          text: contact.type === 'url' ? safeGetHostname(contact.value) : contact.value,
          fontSize: 10,
          link: contact.type === 'url' ? contact.value : undefined,
          color: contact.type === 'url' ? '#207CB1' : '#3C3C3C',
          margin: [0, 0, 0, 2] as [number, number, number, number],
        }));

        targetContent.push({
          stack: [
            { text: source.name, bold: true, margin: [0, 5, 0, 2] },
            { text: source.description, fontSize: 10, color: '#646464', margin: [0, 0, 0, 2] },
            ...contactsContent,
          ],
          margin: [0, 0, 0, 8],
        });
      });
    };

    const offlineHelp = helpSources.filter((h) => h.source === 'offline');
    const onlineHelp = helpSources.filter((h) => h.source === 'online');

    const helpSourcesContent: Content[] = [];
    addHelpSourceSection(offlineHelp, sectionTitles.offlineHelp, helpSourcesContent);
    addHelpSourceSection(onlineHelp, sectionTitles.onlineHelp, helpSourcesContent);

    const documentDefinition: TDocumentDefinitions = {
      content: [
        { text: documentTitle, style: 'title', margin: [0, 0, 0, 10] },
        { text: `${dateStr} ${timeStr}`, style: 'subtitle', margin: [0, 0, 0, 20] },
        {
          text: sectionTitles.victimProfile.toUpperCase(),
          style: 'section',
          margin: [0, 0, 0, 10],
        },
        ...(Array.isArray(victimProfileContent) ? victimProfileContent : [victimProfileContent]),
        {
          text: sectionTitles.crimeOverview.toUpperCase(),
          style: 'section',
          margin: [0, 15, 0, 10],
        },
        ...(Array.isArray(crimeOverviewContent) ? crimeOverviewContent : [crimeOverviewContent]),
        {
          text: sectionTitles.immediateActions.toUpperCase(),
          style: 'section',
          margin: [0, 15, 0, 10],
        },
        {
          ol: immediateActions.map((action) => action.replace(/^\d+\.\s*/, '')),
          margin: [0, 0, 0, 10],
        },
        { text: sectionTitles.helpSources.toUpperCase(), style: 'section', margin: [0, 15, 0, 5] },
        ...helpSourcesContent,
      ],

      styles: {
        title: {
          fontSize: 18,
          bold: true,
          color: '#212121',
        },
        subtitle: {
          fontSize: 10,
          color: '#646464',
        },
        section: {
          fontSize: 14,
          bold: true,
          color: '#212121',
        },
        subsection: {
          fontSize: 12,
          bold: true,
          color: '#212121',
        },
      },

      defaultStyle: {
        fontSize: 11,
        color: '#3C3C3C',
      },

      pageSize: 'A4',
      pageMargins: [40, 40, 40, 40],
    };

    const timestampStr = timestamp.toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const filename = `survey-report-${timestampStr}.pdf`;

    pdfMake.createPdf(documentDefinition).download(filename);
  } catch (error) {
    console.error('Survey PDF generation failed:', error);
    throw new Error(
      `Failed to generate survey PDF: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}
